import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';

import {
  Observable,
  catchError,
  concatMap,
  filter,
  first,
  throwError,
} from 'rxjs';
import { CONFIGURATION } from '../../app.config';
import { AuthService } from './auth.service';
import { AuthUtils } from './auth.utils';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const environment = inject(CONFIGURATION);

  // The CRM runs as its own service but accepts the same token as the API.
  if (
    !req.url.startsWith(environment.apiBaseUrl) &&
    !req.url.startsWith(environment.crmApiBaseUrl)
  ) {
    return next(req);
  }

  if (
    req.url === `${environment.apiBaseUrl}/api/admin/BackofficeAuth/login-token`
  ) {
    return next(req).pipe(
      catchError((error) => {
        // Catch "401 Unauthorized" responses
        if (
          error instanceof HttpErrorResponse &&
          (error.status === 401 ||
            (error.status == 400 && error.error === 'Token is not valid'))
        ) {
          // Sign out
          authService.signOut();

          // Reload the app
          location.reload();
        }

        return throwError(error);
      }),
    );
  }

  if (
    authService.jwtToken &&
    AuthUtils.isTokenExpired(authService.jwtToken) &&
    !authService.isRequestingToken$.value
  ) {
    authService.signInUsingToken().subscribe();
  }

  return authService.isRequestingToken$.pipe(
    filter((isRequestingToken) => !isRequestingToken),
    first(),
    concatMap(() => {
      if (
        authService.jwtToken &&
        !AuthUtils.isTokenExpired(authService.jwtToken)
      ) {
        req = req.clone({
          headers: req.headers.set(
            'Authorization',
            'Bearer ' + authService.jwtToken,
          ),
        });
      }

      // Response
      return next(req).pipe(
        catchError((error) => {
          // Catch "401 Unauthorized" responses
          if (error instanceof HttpErrorResponse && error.status === 401) {
            // Sign out
            authService.signOut();

            // Reload the app
            location.reload();
          }

          return throwError(error);
        }),
      );
    }),
  );
};
